// WF-FIX (2026-10-26): what the Agents (AG) list and an agent's own page share — the change
// mutation, the Reassign / Edit choices (every page of the DEs, people and teams, loaded only
// for someone who may change an agent), the Edit dialog, the runtime and activity labels, and
// the actions themselves: hover icons on a row, labelled buttons on the agent's page. Every
// lifecycle action says what it is: it changes the CompanyOS record (status, reporting), never
// the Hermes container, which keeps running in ClawPanel (Andrei: honest labels).
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArchiveIcon,
  ArrowRightLeftIcon,
  EraserIcon,
  HandIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
} from "lucide-react";
import { type FormEvent, useEffect, useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { WorkforceAgentRow } from "../../api/types";
import { teamsQuery } from "../chart/chart-queries";
import { type CompanyAccess, isManager } from "../company";
import { problemText } from "../de/de-status";
import { formatDateTime, formatRecent } from "../format";
import { myMembershipId } from "../home/home-queries";
import { digitalEmployeesQuery, keys, peopleQuery } from "../queries";
import { useSession } from "../use-feature";
import { RowAction } from "./agents-ux";
import { usePurge } from "./purge-dialog";

export const AG_STATUS = {
  active: { label: "Active", variant: "success" },
  paused: { label: "Paused", variant: "warning" },
  retired: { label: "Retired", variant: "neutral" },
  not_deployed: { label: "Not deployed", variant: "neutral" },
} as const;

/** What every lifecycle action says (the record changes; the container does not). */
export const AG_RECORD_HINT =
  "Changes the CompanyOS record only. The Hermes container keeps running (ClawPanel).";

export type AgPatch = Partial<{
  name: string;
  team_id: string | null;
  status: WorkforceAgentRow["status"];
  reports_to_membership_id: string;
}>;

export type AgTarget = { id: string; name: string; kind: "digital_employee" | "person" };
export type AgChoices = {
  /** Every page of the DEs, people and teams read (`ready`), still reading, or a read failed. */
  status: "loading" | "error" | "ready";
  retry: () => void;
  des: AgTarget[];
  people: AgTarget[];
  teams: { id: string; name: string }[];
};

/** "Hermes · hermes-content-studio-copy-11 · ClawPanel #11" (null: not recorded). */
export function runtimeLabel(r: WorkforceAgentRow["runtime"]): string | null {
  if (!r) return null;
  return ["Hermes", r.container, r.clawpanel_instance ? `ClawPanel #${r.clawpanel_instance}` : null]
    .filter(Boolean)
    .join(" · ");
}

/** The latest activity as a person reads it: "Drafted a post · Oct 20" (null: none reported). */
export function activityLabel(
  a: Pick<WorkforceAgentRow, "last_activity" | "last_activity_at">,
  now = new Date(),
): { text: string; when: string | null; full: string | null } | null {
  if (!a.last_activity && !a.last_activity_at) return null;
  return {
    text: a.last_activity ?? "Active",
    when: a.last_activity_at ? formatRecent(a.last_activity_at, now) : null,
    full: a.last_activity_at ? formatDateTime(a.last_activity_at) : null,
  };
}

/** Every page of an infinite list, fetched to the end (a company has tens of DEs/people/teams). */
export function useAllPages<T>(q: {
  data?: { pages: { data: T[] }[] } | undefined;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  fetchNextPage: () => unknown;
}): T[] {
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = q;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);
  return useMemo(() => q.data?.pages.flatMap((p) => p.data) ?? [], [q.data]);
}

/** Reassign and Edit choices, loaded (every page) only when `enabled` (the viewer may change). */
export function useAgChoices(api: Api, co: string, enabled: boolean): AgChoices {
  const desQ = useInfiniteQuery({ ...digitalEmployeesQuery(api, co), enabled });
  const peopleQ = useInfiniteQuery({ ...peopleQuery(api, co), enabled });
  const teamsQ = useInfiniteQuery({ ...teamsQuery(api, co), enabled });
  const desAll = useAllPages(desQ);
  const peopleAll = useAllPages(peopleQ);
  const teamsAll = useAllPages(teamsQ);
  // WF-FIX (Codex r3): whether the lists are complete, so a picker never passes a failed or
  // half-read list off as the whole choice; `retry` resumes each failed read where it stopped.
  const qs = [desQ, peopleQ, teamsQ];
  const status: AgChoices["status"] = qs.some((q) => q.isError)
    ? "error"
    : qs.every((q) => q.isSuccess && !q.hasNextPage)
      ? "ready"
      : "loading";
  const retry = () => {
    for (const q of qs)
      if (q.isFetchNextPageError) void q.fetchNextPage();
      else if (q.isError) void q.refetch();
  };
  const lists = useMemo(
    () => ({
      // An agent may report to an active or onboarding DE (the API checks its seat and chain).
      des: desAll
        .filter((d) => d.status === "active" || d.status === "onboarding")
        .map(
          (d): AgTarget => ({
            id: d.membership_id,
            name: d.display_name,
            kind: "digital_employee",
          }),
        ),
      people: peopleAll
        .filter((p) => p.kind === "person" && p.status === "active")
        .map((p): AgTarget => ({ id: p.id, name: p.display_name, kind: "person" })),
      teams: teamsAll.map((t) => ({ id: t.id, name: t.name })),
    }),
    [desAll, peopleAll, teamsAll],
  );
  return { ...lists, status, retry };
}

/** PATCH an agent; a refusal toasts its reason. Refreshes the list and the agent's own read. */
export function useAgChange(api: Api, co: string, a: Pick<WorkforceAgentRow, "id" | "name">) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AgPatch) =>
      api.client.workforceAgents.update({ params: { co, id: a.id }, body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.workforceAgents(co) }),
    onError: (e) =>
      toast.add({ title: problemText(e, `${workerName(a.name)} was not changed.`), type: "error" }),
  });
}

/** Rename an agent or move it to another team (only the changed fields are sent). */
export function AgEditDialog({
  agent: a,
  teams,
  pending,
  onClose,
  onSave,
}: {
  agent: WorkforceAgentRow;
  teams: { id: string; name: string }[];
  pending: boolean;
  onClose: () => void;
  onSave: (body: AgPatch) => void;
}) {
  const id = useId();
  const [name, setName] = useState(a.name);
  const [team, setTeam] = useState(a.team_id ?? "");
  // The current team stays a choice even before the teams list has loaded.
  const options = a.team && !teams.some((t) => t.id === a.team?.id) ? [a.team, ...teams] : teams;
  const body: AgPatch = {
    ...(name.trim() !== a.name ? { name: name.trim() } : {}),
    ...((team || null) !== a.team_id ? { team_id: team || null } : {}),
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (Object.keys(body).length) onSave(body);
    else onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Edit {workerName(a.name)}</DialogTitle>
            <DialogDescription>The agent's name and team.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
            <Input
              id={`${id}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-team`}>Team</FieldLabel>
            <NativeSelect id={`${id}-team`} value={team} onChange={(e) => setTeam(e.target.value)}>
              <NativeSelectOption value="">No team</NativeSelectOption>
              {[...options]
                .sort((x, y) => x.name.localeCompare(y.name))
                .map((t) => (
                  <NativeSelectOption key={t.id} value={t.id}>
                    {t.name}
                  </NativeSelectOption>
                ))}
            </NativeSelect>
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || pending}>
              {pending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The Reassign menu's items: the active DEs, then the active people, minus the current one. */
export function ReassignItems({
  choices,
  current,
  onPick,
}: {
  choices: AgChoices;
  current: string;
  onPick: (membershipId: string) => void;
}) {
  const des = choices.des.filter((t) => t.id !== current);
  const people = choices.people.filter((t) => t.id !== current);
  // WF-FIX (Codex r3): never a half-read list passed off as every choice.
  if (choices.status === "loading") return <DropdownMenuItem disabled>Loading…</DropdownMenuItem>;
  if (choices.status === "error")
    return (
      <>
        <DropdownMenuItem disabled>The DEs and people could not be read</DropdownMenuItem>
        <DropdownMenuItem closeOnClick={false} onClick={choices.retry}>
          Retry
        </DropdownMenuItem>
      </>
    );
  // A menu label must sit in a group (Base UI throws otherwise: the old roster's Reassign menu
  // crashed on open — one of the "buttons that don't work").
  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel>Report to a digital employee</DropdownMenuLabel>
        {des.length ? (
          des.map((d) => (
            <DropdownMenuItem key={d.id} onClick={() => onPick(d.id)}>
              <WorkerBadge kind="de" size="sm" />
              {workerName(d.name)}
            </DropdownMenuItem>
          ))
        ) : (
          <DropdownMenuItem disabled>No other active DE</DropdownMenuItem>
        )}
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuLabel>Report to a person</DropdownMenuLabel>
        {people.length ? (
          people.map((p) => (
            <DropdownMenuItem key={p.id} onClick={() => onPick(p.id)}>
              <WorkerBadge kind="person" size="sm" />
              {p.name}
            </DropdownMenuItem>
          ))
        ) : (
          <DropdownMenuItem disabled>No other active person</DropdownMenuItem>
        )}
      </DropdownMenuGroup>
    </>
  );
}

/**
 * The actions on one agent (shown only when `can_edit`): Pause / Resume / Reactivate, Take over
 * (report to me), Reassign, Edit, Retire. `row`: the list's hover icons; `page`: the agent
 * page's labelled buttons.
 */
export function AgActions({
  api,
  company,
  agent: a,
  choices,
  variant,
}: {
  api: Api;
  company: CompanyAccess;
  agent: WorkforceAgentRow;
  choices: AgChoices;
  variant: "row" | "page";
}) {
  const { me } = useSession();
  const mine = myMembershipId(me, company);
  const [editing, setEditing] = useState(false);
  const change = useAgChange(api, company.id, a);
  // PURGE-RETIRED-AGENTS: a retired agent is removed for good by a manager.
  const purge = usePurge(api, company.id, { kind: "workforce_agent", id: a.id, name: a.name });
  if (!a.can_edit) return null;
  const name = workerName(a.name);
  const busy = change.isPending;
  const toggle =
    a.status === "active"
      ? { icon: PauseIcon, label: "Pause", next: "paused" as const }
      : {
          icon: PlayIcon,
          label:
            a.status === "retired"
              ? "Reactivate"
              : a.status === "not_deployed"
                ? "Mark deployed"
                : "Resume",
          next: "active" as const,
        };
  const actions: {
    key: string;
    icon: typeof PauseIcon;
    label: string;
    aria: string;
    hint?: string;
    danger?: boolean;
    onClick: () => void;
  }[] = [
    {
      key: "toggle",
      icon: toggle.icon,
      label: toggle.label,
      aria: `${toggle.label} ${name}`,
      hint: AG_RECORD_HINT,
      onClick: () => change.mutate({ status: toggle.next }),
    },
    ...(mine && a.reports_to_membership_id !== mine
      ? [
          {
            key: "take-over",
            icon: HandIcon,
            label: "Take over",
            aria: `Take over ${name} (report to me)`,
            hint: "It reports to you from now on.",
            onClick: () => change.mutate({ reports_to_membership_id: mine }),
          },
        ]
      : []),
  ];
  const tail = [
    {
      key: "edit",
      icon: PencilIcon,
      label: "Edit",
      aria: `Edit ${name}`,
      onClick: () => setEditing(true),
    },
    ...(a.status !== "retired"
      ? [
          {
            key: "retire",
            icon: ArchiveIcon,
            label: "Retire",
            aria: `Retire ${name}`,
            hint: AG_RECORD_HINT,
            danger: true,
            onClick: () => change.mutate({ status: "retired" }),
          },
        ]
      : isManager(company)
        ? [
            {
              key: "purge",
              icon: EraserIcon,
              label: "Remove permanently",
              aria: `Remove ${name} permanently`,
              danger: true,
              onClick: purge.start,
            },
          ]
        : []),
  ];
  const button = (x: (typeof actions)[number]) =>
    variant === "row" ? (
      <RowAction
        key={x.key}
        icon={x.icon}
        label={x.aria}
        {...(x.hint ? { hint: x.hint } : {})}
        disabled={busy}
        onClick={x.onClick}
      />
    ) : (
      <Tooltip key={x.key}>
        <TooltipTrigger
          render={
            <Button
              variant={x.danger ? "ghost" : "secondary"}
              size="sm"
              aria-label={x.aria}
              {...(x.hint ? { "aria-description": x.hint } : {})}
              data-ag-action={x.key}
              className={x.danger ? "hover:text-danger-text" : undefined}
              disabled={busy}
              onClick={x.onClick}
            />
          }
        >
          <x.icon aria-hidden="true" />
          <span className="max-sm:sr-only">{x.label}</span>
        </TooltipTrigger>
        <TooltipContent>{x.hint ?? x.aria}</TooltipContent>
      </Tooltip>
    );
  return (
    <>
      {actions.map(button)}
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger
            render={
              <DropdownMenuTrigger
                render={
                  <Button
                    variant={variant === "row" ? "ghost" : "secondary"}
                    size={variant === "row" ? "icon-sm" : "sm"}
                    aria-label={`Reassign ${name}`}
                    data-ag-action="reassign"
                    disabled={busy}
                  />
                }
              />
            }
          >
            <ArrowRightLeftIcon aria-hidden="true" />
            {variant === "page" ? <span className="max-sm:sr-only">Reassign</span> : null}
          </TooltipTrigger>
          <TooltipContent>Reassign: whom it reports to</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="min-w-48">
          <ReassignItems
            choices={choices}
            current={a.reports_to_membership_id}
            onPick={(id) => change.mutate({ reports_to_membership_id: id })}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      {tail.map(button)}
      {purge.dialog}
      {editing ? (
        <AgEditDialog
          agent={a}
          teams={choices.teams}
          pending={busy}
          onClose={() => setEditing(false)}
          onSave={(body) => change.mutate(body, { onSuccess: () => setEditing(false) })}
        />
      ) : null}
    </>
  );
}
